<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Navigation/NavigationLink.vue
import type { Schemas } from '@shopware/api-client/api-types';
import { getCategoryRoute, getTranslatedProperty } from '@shopware-pwa/helpers-next';

const props = withDefaults(
    defineProps<{
        navigationElement: Schemas['Category'];
        activeClasses?: string | null;
        activeWithExactMatch?: boolean;
        classes?: string | null;
        asLink?: boolean;
        asAllItemsLink?: boolean;
        displayIcon?: boolean;
        useSlot?: boolean;
    }>(),
    {
        activeClasses: null,
        activeWithExactMatch: true,
        classes: null,
        asLink: true,
        asAllItemsLink: false,
        displayIcon: false,
        useSlot: false,
    },
);
const emit = defineEmits<{
    click: [navigationElement: Schemas['Category'], categoryLink: string | null];
}>();
const { resolveLink, isInternalLink, isExternalLink, externalLink } = useNavigationLinkResolver(
    props.navigationElement,
);
const { url } = useCountrySwitch();

const categoryLink = computed(() => {
    if (isExternalLink && externalLink) {
        return externalLink;
    }

    if (isInternalLink) {
        const link = resolveLink();
        return url(link.path);
    }

    return url(getCategoryRoute(props.navigationElement));
});

const handleClick = () => {
    trackNavigation(
        props.navigationElement.level ? props.navigationElement.level - 1 : 0,
        getTranslatedProperty(props.navigationElement, 'name'),
    );
    emit('click', props.navigationElement, categoryLink.value);
};

const { isActive } = useActivePath();
const { trackNavigation } = useAnalytics();
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<NuxtLink
        v-if="asLink"
        :target="isExternalLink ? '_blank' : ''"
        :rel="isExternalLink ? 'noopener noreferrer nofollow' : ''"
        :aria-label="getTranslatedProperty(navigationElement, 'name')"
        :to="categoryLink"
        :format="!isExternalLink"
        class="transition-all hover:text-brand-primary"
        :class="[classes, isActive(navigationElement.seoUrls, activeWithExactMatch) ? activeClasses : '']"
        @click.prevent="handleClick"
    >
        <template v-if="asAllItemsLink">
            {{ $t('navigation.sidebar.allItems') }}
        </template>

        <slot v-else-if="useSlot" />

        <template v-else>
            {{ getTranslatedProperty(navigationElement, 'name') }}
        </template>
    </NuxtLink>

    <div
        v-else
        :class="[classes, isActive(navigationElement.seoUrls) ? activeClasses : '', displayIcon ? 'flex' : '']"
        @click="$emit('click', navigationElement, null)"
    >
        <span>
            {{ getTranslatedProperty(navigationElement, 'name') }}
        </span>

        <span
            v-if="displayIcon"
            class="ml-auto items-center"
        >
            <!-- IDS-188: change icon, change style -->
            <FormKitIcon
                class="block size-6"
                icon="ids_arrow-right"
            />
        </span>
    </div>
</FormKitLazyProvider>

</template>
